import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import WorkOrderDetail from './WorkOrderDetail';
import WorkOrderForm from './WorkOrderForm';
import CompanyCode from './CompanyCode';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  Clock, 
  User, 
  Building2, 
  Settings, 
  AlertTriangle,
  Filter,
  Search,
  Calendar,
  CheckSquare,
  Plus,
  Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function KanbanBoard({ user }) {
  const { t } = useLanguage();

  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [departments, setDepartments] = useState([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [showWorkOrderForm, setShowWorkOrderForm] = useState(false);

  const fetchWorkOrders = async () => {
    try {
      const response = await axios.get(`${API}/work-orders`);
      // Filter to only show Repair type work orders (exclude PM/Maintenance tasks)
      const repairWorkOrders = response.data.filter(wo => wo.type === 'Repair');
      console.log('Loaded repair work orders:', repairWorkOrders.length, 'of', response.data.length, 'total');
      setWorkOrders(repairWorkOrders);
    } catch (error) {
      console.error('Error fetching work orders:', error);
      toast.error('Failed to load work orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  useEffect(() => {
    fetchWorkOrders();
    fetchDepartments();

    // Listen for work order updates from other pages
    const handleWorkOrderUpdate = (event) => {
      // If no detail provided, just reload all work orders
      if (!event.detail) {
        fetchWorkOrders();
        return;
      }
      
      const { workOrderId, checklist } = event.detail;
      setWorkOrders(prevOrders =>
        prevOrders.map(wo =>
          wo.id === workOrderId
            ? { ...wo, checklist: checklist }
            : wo
        )
      );
    };

    // Listen for work order deletion from Daily Tasks page (when repair task is completed)
    const handleWorkOrderDeleted = (event) => {
      const { workOrderId } = event.detail;
      console.log('KanbanBoard: Received work order deleted event', workOrderId);
      setWorkOrders(prevOrders =>
        prevOrders.filter(wo => wo.id !== workOrderId)
      );
    };

    window.addEventListener('workOrderUpdated', handleWorkOrderUpdate);
    window.addEventListener('workOrderDeleted', handleWorkOrderDeleted);
    
    // Cleanup listeners
    return () => {
      window.removeEventListener('workOrderUpdated', handleWorkOrderUpdate);
      window.removeEventListener('workOrderDeleted', handleWorkOrderDeleted);
    };
  }, []);

  const getPriorityColor = (priority) => {
    const colors = {
      'Critical': 'priority-critical',
      'High': 'priority-high',
      'Medium': 'priority-medium',
      'Low': 'priority-low'
    };
    return colors[priority] || 'bg-gray-500';
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const getCompletedChecklistCount = (checklist) => {
    if (!checklist || checklist.length === 0) return { completed: 0, total: 0 };
    const completed = checklist.filter(item => item.completed).length;
    return { completed, total: checklist.length };
  };

  // Filter work orders
  const filteredWorkOrders = workOrders.filter(wo => {
    const matchesDepartment = !filterDepartment || filterDepartment === 'all' || wo.department_id === filterDepartment;
    const matchesPriority = !filterPriority || filterPriority === 'all' || wo.priority === filterPriority;
    const matchesSearch = !searchTerm || 
      wo.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      wo.wo_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (wo.description && wo.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesDepartment && matchesPriority && matchesSearch;
  });

  const handleWorkOrderClick = (workOrder) => {
    setSelectedWorkOrder(workOrder);
  };

  const handleWorkOrderUpdate = (updatedWorkOrder) => {
    setWorkOrders(prevOrders =>
      prevOrders.map(wo =>
        wo.id === updatedWorkOrder.id ? updatedWorkOrder : wo
      )
    );
    // Update selectedWorkOrder if it's currently open
    if (selectedWorkOrder && selectedWorkOrder.id === updatedWorkOrder.id) {
      setSelectedWorkOrder(updatedWorkOrder);
    }
  };

  const handleDeleteWorkOrder = async (e, workOrderId) => {
    e.stopPropagation(); // Prevent opening the work order detail
    
    if (!window.confirm('Are you sure you want to delete this work order?')) {
      return;
    }

    try {
      await fetch(`${API}/work-orders/${workOrderId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setWorkOrders(prev => prev.filter(wo => wo.id !== workOrderId));
      toast.success('Work order deleted successfully');
    } catch (error) {
      console.error('Failed to delete work order:', error);
      toast.error('Failed to delete work order');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kanban-board">
      {/* Company Code Display */}
      <CompanyCode />
      
      {/* Header with filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex items-center justify-between w-full lg:w-auto">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">{t('repairWorkOrders')}</h2>
              <p className="text-gray-600 mt-1">
                {filteredWorkOrders.length} {t('workOrders')}
              </p>
            </div>
            <Button
              onClick={() => setShowWorkOrderForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white ml-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('addTask')}
            </Button>
          </div>
          
          {/* Filters */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder={t('searchWorkOrders')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full sm:w-64"
                data-testid="search-work-orders"
              />
            </div>
            
            <Select value={filterDepartment} onValueChange={setFilterDepartment}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-department">
                <div className="flex items-center">
                  <Filter className="w-4 h-4 mr-2 text-gray-500" />
                  <SelectValue placeholder={t('allDepartments')} />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allDepartments')}</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-full sm:w-36" data-testid="filter-priority">
                <SelectValue placeholder={t('priority')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="Critical">Critical</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Scheduled Repair Work Orders List */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <h3 className="text-lg font-semibold text-gray-900">Scheduled Repair Work Orders</h3>
            <Badge variant="secondary" className="bg-blue-100 text-blue-700">
              {filteredWorkOrders.length}
            </Badge>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Active repair tasks. When completed from Daily Tasks, they will be automatically removed and saved to history.
          </p>
        </div>

        <div className="p-6">
          {filteredWorkOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Settings className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg mb-2">No repair work orders</p>
              <p className="text-gray-400 text-sm">Click "Create Work Order" to add a new repair task</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredWorkOrders.map(workOrder => {
                const overdue = isOverdue(workOrder.due_date);
                
                return (
                  <Card
                    key={workOrder.id}
                    className="cursor-pointer hover:shadow-lg transition-all duration-200 bg-white border border-gray-200"
                    onClick={() => handleWorkOrderClick(workOrder)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-xs font-medium text-gray-500">
                              {workOrder.wo_id}
                            </span>
                            <Badge className={`text-xs px-2 py-0.5 ${getPriorityColor(workOrder.priority)}`}>
                              {workOrder.priority}
                            </Badge>
                            {overdue && (
                              <Badge className="bg-red-100 text-red-700 text-xs px-2 py-0.5">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Overdue
                              </Badge>
                            )}
                          </div>
                          <h4 className="font-medium text-gray-900 mb-2">{workOrder.title}</h4>
                        </div>
                        <button
                          onClick={(e) => handleDeleteWorkOrder(e, workOrder.id)}
                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete work order"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="space-y-2 text-sm text-gray-600">
                        {workOrder.department_name && (
                          <div className="flex items-center">
                            <Building2 className="w-4 h-4 mr-2 flex-shrink-0" />
                            <span className="truncate">{workOrder.department_name}</span>
                          </div>
                        )}
                        {workOrder.machine_name && (
                          <div className="flex items-center">
                            <Settings className="w-4 h-4 mr-2 flex-shrink-0" />
                            <span className="truncate">{workOrder.machine_name}</span>
                          </div>
                        )}
                        {(workOrder.assignee_names && workOrder.assignee_names.length > 0) ? (
                          <div className="flex items-center">
                            <User className="w-4 h-4 mr-2 flex-shrink-0" />
                            <span className="truncate">{workOrder.assignee_names.join(', ')}</span>
                          </div>
                        ) : workOrder.assignee_name && (
                          <div className="flex items-center">
                            <User className="w-4 h-4 mr-2 flex-shrink-0" />
                            <span className="truncate">{workOrder.assignee_name}</span>
                          </div>
                        )}
                        {workOrder.due_date && (
                          <div className={`flex items-center ${overdue ? 'text-red-600 font-medium' : ''}`}>
                            <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                            Due {formatDate(workOrder.due_date)}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Work Order Detail Modal */}
      {selectedWorkOrder && (
        <WorkOrderDetail
          workOrder={selectedWorkOrder}
          onClose={() => setSelectedWorkOrder(null)}
          onUpdate={handleWorkOrderUpdate}
          user={user}
        />
      )}

      {/* Work Order Form Modal */}
      {showWorkOrderForm && (
        <WorkOrderForm
          onClose={() => setShowWorkOrderForm(false)}
          onWorkOrderCreated={(newWorkOrder) => {
            setWorkOrders(prev => [...prev, newWorkOrder]);
            setShowWorkOrderForm(false);
            toast.success('Work order created successfully!');
          }}
          user={user}
        />
      )}
    </div>
  );
}

export default KanbanBoard;
